import { useEffect } from "react";
import { createFileRoute, redirect, useRouter } from "@tanstack/react-router";

import { specEditSearchSchema } from "@/api/contracts/specifications";
import { SpecEditPage, SpecEditPending } from "@/components/specifications/spec-edit-page";
import { specificationSummaryQueryOptions, useSpecificationSummary } from "@/hooks/specifications";
import { editGuardBranch } from "@/lib/route-guards";
import { SPECIFICATIONS_BASE_PATH, parseRouteId, redirectOffTree } from "@/lib/route-paths";
import { EDIT_PAGE_TITLE, editPageTitle } from "@/lib/spec-display";
import { islandRouteSearchMiddleware, islandSearchNavigation } from "@/lib/item-search-island";
import { specSearchNavigation } from "@/lib/spec-lines-display";

// A version's edit URL — the donor's `SpecificationEditRoute`
// (`routes/specification-edit-route.tsx`) and the page it lands on (#806).
//
// **A sibling, not a child**: the trailing underscore on `$versionId_` keeps
// this route out of the details route, so the details component never renders
// around it — the catalog's `_.edit` precedent. It is a child of the spec
// layout route, so the band's face is decided by this match (#680 Q2).
//
// The guard runs **silently and in the source's order** (`editGuardBranch`):
// missing version → no `mayEdit` → published → archived → editor. The first
// four replace the URL with the read-only view. The donor dispatches two
// strings on the first two branches to a slice nothing reads, so it shows
// nothing; neither does this (#678 Q8). Ids that are not ids leave for the base
// list, the donor's `noAccessUrl` without numbers to build a read URL from.
// The `editor` branch hands the summary to the page.
//
// **On arrival only** (#965 N7): the donor's guard reads a version query no
// editor write refreshes and nothing refetches on focus
// (`specification-edit-route.tsx:20-62`), so it runs when the page loads and
// never mid-session — a draft another tab archives or publishes keeps its
// editor, the next write lands (#851 D8), and only a reload sends it to the
// read-only view. So the loader guards on the router's `enter` — an arrival,
// or a navigation to another version — and not on `stay`, a re-run of the
// same match (the title refresh's `invalidate`, a search change).

export const Route = createFileRoute(
  "/_authed/_chrome/specifications/_spec/$specId/versions/$versionId_/edit",
)({
  // `?specSearch=` — read by the lines body, never by the loader: a search is
  // a new lines query, not a new page. The Item Search island's keys beside it
  // (#811) are the right pane's, read the same way.
  validateSearch: specEditSearchSchema,
  // After that validation, which the router also runs on every search it
  // builds: the schema reads the island's ids and `page` as text, and the
  // writer would quote them (`?mfrs=%2258%22`) unless handed them bare (#895).
  search: { middlewares: [islandRouteSearchMiddleware] },
  loader: async ({ context, params, cause }) => {
    const specId = parseRouteId(params.specId);
    const versionId = parseRouteId(params.versionId);
    if (specId === null || versionId === null) redirectOffTree(SPECIFICATIONS_BASE_PATH);
    const summary = await context.queryClient
      .ensureQueryData(specificationSummaryQueryOptions(specId))
      // A failed read has no version either, which the donor answers the same
      // way: its `currentVersion` is undefined whichever way the read ended.
      .catch(() => null);
    const readOnly = redirect({
      to: "/specifications/$specId/versions/$versionId",
      params: { specId: String(specId), versionId: String(versionId) },
      replace: true,
    });
    if (cause === "stay" && summary) return { summary };
    switch (editGuardBranch(summary, versionId)) {
      case "missing-version":
      case "no-permission":
      case "published":
      case "archived":
        throw readOnly;
      case "editor":
        // The guard has found the version on the summary.
        return { summary: summary! };
    }
  },
  // `Specification Edit | <name> | <division>` (`spec-edit-page.tsx:44-48`),
  // less the product suffix every Atlas title drops (shell #657 D1).
  head: ({ loaderData }) => ({
    meta: [{ title: loaderData ? editPageTitle(loaderData.summary) : EDIT_PAGE_TITLE }],
  }),
  // The donor's `<Loading />` while the spec is read; at once and for no
  // minimum, as the donor swaps it on its loading flag. A cached summary
  // resolves before it shows.
  pendingComponent: SpecEditPending,
  pendingMs: 0,
  pendingMinMs: 0,
  component: SpecEditRoute,
});

function SpecEditRoute() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const loaded = Route.useLoaderData();
  const params = Route.useParams();
  const versionId = parseRouteId(params.versionId);
  // The live query rather than the loader's snapshot, so a mutation's
  // corrected summary (ADR-0014 decision 1; the header write) reaches the
  // heading, the notes and the breadcrumb in the frame it lands.
  const live = useSpecificationSummary(loaded.summary.id, { retryOnMount: false }).data;
  const summary = live ?? loaded.summary;
  // The tab title too, as the donor's live `useSetPageTitle` follows its store
  // (`spec-edit-page.tsx:41-49`): the head reads the loader's snapshot, so a
  // header save that moves the name or division re-runs the loader, which
  // re-reads the summary the save already corrected in the cache.
  const router = useRouter();
  const title = editPageTitle(summary);
  const loadedTitle = editPageTitle(loaded.summary);
  useEffect(() => {
    if (title !== loadedTitle) void router.invalidate();
  }, [title, loadedTitle, router]);
  // No guard here (#965 N7): the loader's ran on arrival, and a live summary
  // that now reads published, archived or uneditable keeps the editor, as the
  // donor's never-refreshed guard query does. The version is the live
  // summary's, or the arrival's should the live one stop listing it.
  const version =
    summary.versions.find((candidate) => candidate.id === versionId) ??
    loaded.summary.versions.find((candidate) => candidate.id === versionId);
  if (!version) return null;
  return (
    <SpecEditPage
      // Every arrival mounts the editor pane at the top (#806 Assumption 9).
      key={`${summary.id}:${version.id}`}
      read={{ summary, version }}
      search={search}
      onSearchChange={(term) => void navigate(specSearchNavigation(term))}
      onIslandSearchChange={(patch) => void navigate(islandSearchNavigation(patch))}
    />
  );
}
